import { useMutation } from "@tanstack/react-query";
import {
  ApiError,
  createCoachingNote,
  updateCoachingNote,
} from "@/lib/api";
import { invalidateScope, queryClient } from "@/lib/queryClient";
import type {
  CoachingNote,
  CreateCoachingNoteRequest,
  UpdateCoachingNoteRequest,
} from "@/lib/types";

/**
 * Save (create OR update) a coaching note.
 *
 * The mutation accepts a discriminated input: when `id` is provided
 * it PATCHes; when omitted it POSTs. The backend's update request
 * model has `extra="forbid"` so the body must NOT include `match_id`
 * / `slot` / `timestamp_seconds` (those are CREATE-only — a note's
 * moment binding is immutable after creation). The caller (the
 * Coach Review shell) builds the right shape per mode.
 *
 * Cache invalidation: on success we invalidate the SCOPED query
 * cache so the coach-review-bundle (keyed on match_id/note_id) +
 * any open coach-notes lists refetch with the new state. Mutation
 * results are NOT optimistic — the M4 shell isn't tracking the
 * note list in a way that justifies optimistic updates yet (the
 * list pages land in M6). Save-then-refetch keeps the contract
 * simple.
 *
 * Error path: surfaces ApiError to the caller via `onError` /
 * the `error` field. The composer disables the submit button while
 * the mutation is in flight (RHF's `formState.isSubmitting`
 * tracks the returned promise automatically).
 */

type SaveNoteInput =
  | { mode: "create"; body: CreateCoachingNoteRequest }
  | { mode: "update"; id: number; body: UpdateCoachingNoteRequest };

export function useSaveCoachingNote() {
  return useMutation<{ note: CoachingNote }, ApiError, SaveNoteInput>({
    mutationFn: (input) =>
      input.mode === "create"
        ? createCoachingNote(input.body)
        : updateCoachingNote(input.id, input.body),
    onSuccess: async (response, input) => {
      // Refresh the bundle (which carries the seed note in edit
      // mode) + any open coach-notes lists. invalidateScope is the
      // cheap "everything scoped to the current team" hammer; the
      // bundle alone wouldn't catch a notes-list page that's also
      // open in another tab.
      await invalidateScope();
      // Also surgical-invalidate the bundle query specifically —
      // helps when invalidateScope's predicate misses something
      // queued before the mutation started.
      if (input.mode === "update") {
        queryClient.invalidateQueries({
          queryKey: ["coach-review-bundle"],
        });
      }
      return response;
    },
  });
}
